import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceCancellationCard } from '@/components/AbsenceApplication/AbsenceCancellationCard';
import { AbsenceDetailCard } from '@/components/AbsenceApplication/AbsenceDetailCard';
import { AbsenceStatusChip } from '@/components/AbsenceApplication/AbsenceStatusChip';
import { useAbsenceCancelFlow } from '@/components/AbsenceApplication/hooks/useAbsenceCancelFlow';
import { useNonAttendanceApplication } from '@/components/AbsenceApplication/hooks/useNonAttendanceApplication';
import { useRetryCateringCancellation } from '@/components/AbsenceApplication/hooks/useRetryCateringCancellation';
import { Button } from '@/components/Button';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { Close } from '@/components/Icons/Close';
import { ConfirmModal } from '@/components/Modal/ConfirmModal';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { BorderRadius, ButtonVariant, Size } from '@/types/components';
import { formatDateRange } from '@/utils/date';

interface Props {
  applicationId: number;
  hideBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
}

export const AbsenceDetailView: React.FC<Props> = ({
  applicationId,
  hideBackButton = false,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();
  const { guardAction } = useProfileRestriction();
  const { data: application, isPolling } = useNonAttendanceApplication({
    id: applicationId,
  });
  const { mutate: retryCatering, isPending: isRetrying } =
    useRetryCateringCancellation();
  const {
    isCancelable,
    showConfirmDisclaimer,
    isConfirmOpen,
    isCanceling,
    setIsConfirmOpen,
    confirmCancel,
  } = useAbsenceCancelFlow(application);
  const [hasRetried, setHasRetried] = useState(false);
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();

  if (!application) {
    return (
      <div className="flex h-full flex-col">
        <header className="flex shrink-0 items-center gap-3 bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onBack}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>
          {!hideBackButton && (
            <button
              type="button"
              aria-label={t('global.back')}
              onClick={onBack}
              className="hidden size-4 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
            >
              <ChevronRight className="size-4 rotate-180" strokeWidth="2" />
            </button>
          )}
        </header>
        <div className="flex flex-1 items-center justify-center">
          <PageSpinner className="size-[20px] text-subtle-foreground" />
        </div>
      </div>
    );
  }

  const isCanceled = application.isCanceled;
  const period = formatDateRange(
    application.absencePeriod.from,
    application.absencePeriod.to,
  );
  const isCardLoading = isRetrying || (isPolling && hasRetried);

  const handleRetry = () => {
    setHasRetried(true);
    retryCatering(application.id);
  };

  return (
    <div className="flex h-full flex-col">
      <header className="relative flex shrink-0 items-center gap-3 bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
        <button
          type="button"
          aria-label={t('global.back')}
          onClick={onBack}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <ArrowLeft />
        </button>
        {!hideBackButton && (
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onBack}
            className="hidden size-4 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <ChevronRight className="size-4 rotate-180" strokeWidth="2" />
          </button>
        )}
        <h2
          style={{ opacity: scrollProgress }}
          className="text-callout-16 pointer-events-none absolute left-1/2 -translate-x-1/2 text-foreground transition-opacity duration-75 md:hidden"
        >
          {period}
        </h2>
        <h2 className="text-headline-20 hidden min-w-0 flex-1 truncate text-foreground md:block">
          {period}
        </h2>
        <AbsenceStatusChip isCanceled={isCanceled} hideOnMobile />
        <button
          type="button"
          aria-label={t('global.close')}
          onClick={onClose}
          className="hidden size-4 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close />
        </button>
      </header>

      <div
        onScroll={handleScroll}
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-5 md:px-5 md:pt-4"
      >
        <div className="flex flex-col items-start gap-2 md:hidden">
          <h2
            style={{ opacity: 1 - scrollProgress }}
            className="text-title-36 text-foreground transition-opacity duration-75"
          >
            {period}
          </h2>
          <AbsenceStatusChip isCanceled={isCanceled} />
        </div>

        {isCanceled && (
          <AbsenceCancellationCard
            application={application}
            isRetrying={isCardLoading}
            hideRetryButton
            onRetry={handleRetry}
          />
        )}
        <AbsenceDetailCard
          application={application}
          isRetrying={isCardLoading}
          isDimmed={isCanceled}
          hideRetryButton
          onRetry={handleRetry}
        />
      </div>

      {isCancelable && (
        <div className="flex shrink-0 border-t border-divider bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:justify-end md:border-t-0 md:px-5 md:pt-5 md:pb-5">
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.Large}
            radius={BorderRadius.Small}
            onClick={() => guardAction(() => setIsConfirmOpen(true))}
            className="h-14 w-full text-destructive md:h-10 md:w-[220px] md:rounded-3xl"
          >
            {t('absenceApplication.detail.cancelApplication')}
          </Button>
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        title={t('absenceApplication.cancelConfirm.title')}
        description={t('absenceApplication.cancelConfirm.description')}
        disclaimer={
          showConfirmDisclaimer
            ? t('absenceApplication.cancelConfirm.disclaimer')
            : undefined
        }
        cancelLabel={t('global.cancel')}
        confirmLabel={t('absenceApplication.cancelConfirm.confirm')}
        isPending={isCanceling}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmCancel}
      />
    </div>
  );
};
